import React, { useEffect, useState, useCallback } from 'react';
import LayoutComponent from '../../dashboard/Layout'; // Changed import
import ConstructionProgressHeader from './Header';
import { useAtom } from 'jotai';
import dashboardAtoms, { fetchMapRender, fetchBlocks } from '../../../atoms/Dashboard/DashboardAtom';
import { constructionSequenceAtom, fetchConstructionSequences, updateConstructionSequences, deleteConstructionSequenceAtom } from '../../../atoms/setup/ConstructionSequenceAtom';
import { CircularProgress, Box, Button, Modal, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Paper, IconButton, Menu, MenuItem } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ConstructionProgressPopup from './ConstructionProgressPopup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { EllipsisVertical } from 'lucide-react';
import settingsAtom from '../../../atoms/settings/SettingsAtom';

const ConstructionProgressCard = () => {
  const [data] = useAtom(dashboardAtoms);
  const svgProgressBlockWise = data.svgProgressBlockWise || [];
  const [, fetchSvgMap] = useAtom(fetchMapRender);
  const [, fetchBlocksData] = useAtom(fetchBlocks);
  const [sequenceState] = useAtom(constructionSequenceAtom);
  const [, fetchSequences] = useAtom(fetchConstructionSequences);
  const [, updateSequences] = useAtom(updateConstructionSequences);
  const [, deleteSequence] = useAtom(deleteConstructionSequenceAtom);
  const [editMode, setEditMode] = useState(false);
  const [sequenceData, setSequenceData] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [, setTableDraft] = useState([]);
  const [originalSequences, setOriginalSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectBlock, setSelectBlock] = useState(false);
  const [selectPart, setSelectPart] = useState([]);
  const [isSelect, setIsSelect] = useState(false);
  const [, setHighlightedBlocks] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentRowIdx, setCurrentRowIdx] = useState(null);
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));
  const timezone = currentProject?.timezone || 'Pacific/Auckland';

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchSvgMap(),
          fetchBlocksData(),
          fetchSequences()
        ]);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    initializeData();
  }, [fetchSvgMap, fetchBlocksData, fetchSequences]);

  useEffect(() => {
    if (sequenceState.sequences && data.blocks && !sequenceState.loading) {
      const formattedSequences = sequenceState.sequences.map(seq => {
        const block = data.blocks.find(b => b.id === seq.block_id);
        return {
          id: seq.id,
          blockId: seq.block_id,
          blockName: block ? block.name : `Block ${seq.block_id}`,
          startWeek: seq.start_time,
          endWeek: seq.end_time,
          sequence: seq.sequence,
          originalData: {
            id: seq.id,
            startWeek: seq.start_time,
            endWeek: seq.end_time,
            sequence: seq.sequence
          }
        };
      });
      
      setSequenceData(formattedSequences);
      setOriginalSequences([...formattedSequences]);
      
      const uniqueBlockIds = [...new Set(sequenceState.sequences.map(seq => seq.block_id))];
      setSelectPart(uniqueBlockIds);
      
      const blocksWithData = formattedSequences.map(seq => seq.blockId);
      setHighlightedBlocks(blocksWithData);
    }
  }, [sequenceState.sequences, data.blocks, sequenceState.loading]);

  useEffect(() => {
    const blocksWithData = sequenceData.map(seq => seq.blockId);
    setHighlightedBlocks(blocksWithData);
  }, [sequenceData]);

  const handleBlockClick = useCallback((block) => {
    if (!editMode || !block) {
      return;
    }

    const blockId = typeof block === 'object' ? block.id : block;
    if (!blockId) return;

    let blockObject = block;
    if (typeof block !== 'object' || !block.name) {
      blockObject = data.blocks?.find(b => b.id === blockId) || { id: blockId, name: `Block ${blockId}` };
    }

    const existingSequences = sequenceData.filter(seq => seq.blockId === blockId);

    if (existingSequences.length === 0) {
      setSelectPart((prev) => {
        if (!prev.includes(blockId)) {
          return [...prev, blockId];
        }
        return prev;
      });

      setSelectedBlock({
        ...blockObject,
        existingSequence: null
      });
    } else {
      setSelectedBlock({
        ...blockObject,
        existingSequence: existingSequences[0],
        allSequences: existingSequences
      });
    }

    setPopupOpen(true);
  }, [editMode, sequenceData, data.blocks]);

  const handlePopupSubmit = async (payload) => {
    if (!selectedBlock) return;

    const startTimestamp = payload.startWeek;
    const endTimestamp = payload.endWeek;
    const blockId = selectedBlock.id;

    setSequenceData((prev) => {
      let newData = [...prev];

      if (payload.id) {
        const existingIndex = newData.findIndex((row) => row.id === payload.id);
        if (existingIndex !== -1) {
          newData[existingIndex] = {
            ...newData[existingIndex],
            startWeek: startTimestamp,
            endWeek: endTimestamp,
            modified: true,
            id: payload.id
          };
          return newData;
        }
      }

      if (selectedBlock.existingSequence && selectedBlock.existingSequence.id) {
        const existingIndex = newData.findIndex((row) => row.id === selectedBlock.existingSequence.id);
        if (existingIndex !== -1) {
          newData[existingIndex] = {
            ...newData[existingIndex],
            startWeek: startTimestamp,
            endWeek: endTimestamp,
            modified: true,
            id: selectedBlock.existingSequence.id
          };
          return newData;
        }
      }

      const maxSequence = Math.max(0, ...newData.map(seq => seq.sequence || 0));
      const nextSequence = maxSequence + 1;

      const newSequence = {
        blockId: blockId,
        blockName: selectedBlock.name || `Block ${blockId}`,
        startWeek: startTimestamp,
        endWeek: endTimestamp,
        sequence: nextSequence,
        modified: true,
        isNew: true,
        id: null
      };

      newData.push(newSequence);
      return newData;
    });

    setPopupOpen(false);
    setSelectedBlock(null);
    
    setTimeout(() => {
      const updatedBlocksWithData = sequenceData.map(seq => seq.blockId);
      if (!updatedBlocksWithData.includes(blockId)) {
        updatedBlocksWithData.push(blockId);
      }
      setHighlightedBlocks(updatedBlocksWithData);
    }, 100);
  };

  const handleEditSequence = () => {
    setTableDraft([...sequenceData]);
    setEditMode(true);
    setSelectBlock(true);
    setIsSelect(true);
    
    const currentHighlighted = sequenceData.map(seq => seq.blockId);
    setHighlightedBlocks(currentHighlighted);
  };

  const handleCancel = () => {
    setSequenceData(originalSequences);
    const blockIds = originalSequences.map(seq => seq.blockId);
    setSelectPart(blockIds);
    setEditMode(false);
    setSelectBlock(false);
    setIsSelect(false);
    setEditRow(null);
    setPopupOpen(false);
    setSelectedBlock(null);

    const originalHighlighted = originalSequences.map(seq => seq.blockId);
    setHighlightedBlocks(originalHighlighted);
  };

  const handleSave = async () => {
    try {
      const sequencesToSave = sequenceData
        .filter(seq => seq.delete !== true && seq.modified === true)
        .map((seq) => {
          const payload = {
            start_time: seq.startWeek,
            end_time: seq.endWeek,
            block_id: seq.blockId,
            color: '#FFFF00'
          };

          if (seq.id) {
            payload.id = seq.id;
            payload.sequence = seq.sequence;
          } else {
            payload.sequence = seq.sequence;
          }

          return payload;
        });


      if (sequencesToSave.length > 0) {
        await updateSequences(sequencesToSave);
      }
      
      setEditMode(false);
      setSelectBlock(false);
      setIsSelect(false);
      setEditRow(null);
      setTableDraft([]);

      const savedBlocksWithData = sequenceData.map(seq => seq.blockId);
      setHighlightedBlocks(savedBlocksWithData);
    } catch (error) {
      alert('Error saving sequence. Please try again.');
    }
  };

  const handleTableEdit = (idx) => {
    setEditRow(idx);
  };

  const handleTableChange = (idx, field, value) => {
    setSequenceData(prev => {
      const newData = [...prev];
      const timestamp = dayjs(value).tz(timezone);
      const existingSequence = newData[idx];
      
      newData[idx] = { 
        ...existingSequence, 
        [field]: timestamp, 
        modified: true,
        id: existingSequence.id
      };
      
      return newData;
    });
  };

  const handleDeleteSequence = async (sequenceToDelete) => {
    try {
      if (sequenceToDelete.id) {
        await deleteSequence({
          id: sequenceToDelete.id,
          sequence: sequenceToDelete.sequence,
          startWeek: sequenceToDelete.startWeek,
          endWeek: sequenceToDelete.endWeek,
          blockId: sequenceToDelete.blockId,
          color: '#FFFF00'
        });
      } else {
        setSequenceData((prev) =>
          prev.filter((seq) => seq.blockId !== sequenceToDelete.blockId)
        );
        setSelectPart((prev) =>
          prev.filter((blockId) => blockId !== sequenceToDelete.blockId)
        );
        setHighlightedBlocks((prev) =>
          prev.filter((blockId) => blockId !== sequenceToDelete.blockId)
        );
      }
    } catch (error) {
      alert('Error deleting sequence. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return dayjs.unix(timestamp).tz(timezone).format('DD MMM YYYY');
  };

  const isMonday = (date) => {
    return date && date.getDay() === 1;
  };

  const timestampToDate = (timestamp) => {
    if (!timestamp) return null;
    return dayjs.unix(timestamp).tz(timezone).toDate();
  };

  const dateToYMD = (date) => {
    if (!date) return '';
    return dayjs(date).tz(timezone).format('YYYY-MM-DD');
  };

  const handleSvgBlockClick = useCallback((blockData) => {
    if (editMode) {
      handleBlockClick(blockData);
    } else {
      const blockId = typeof blockData === 'object' ? blockData.id : blockData;
      setHighlightedBlocks((prev) => {
        if (prev.includes(blockId)) {
          return prev.filter((id) => id !== blockId);
        } else {
          return [...prev, blockId];
        }
      });
    }
  }, [editMode, handleBlockClick]);

  const handleSaveRow = (updatedRow) => {
    const updatedSequenceData = sequenceData.map((row) =>
      row.id === updatedRow.id ? { ...row, ...updatedRow } : row
    );
    setSequenceData(updatedSequenceData);
    setEditRow(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{height: 'calc(100vh - 200px)'}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data.svgMap) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography>No SVG map data available</Typography>
      </Box>
    );
  }

  const enhancedData = {
    ...data,
    svgProgressBlockWise: svgProgressBlockWise,
    overAllSVG: sequenceData.reduce((acc, seq) => {
      if (seq.blockId) {
        acc[seq.blockId] = [{
          activity_id: 'construction_sequence',
          progress: 0,
          color: '#FFFF00',
        }];
      }
      return acc;
    }, {})
  };

  const tableContainerStyle = {
    height: 'calc(100vh - 200px)',
    overflowY: 'auto',
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%' }}>
        <ConstructionProgressHeader
          editMode={editMode}
          onEditSequence={handleEditSequence}
          onSave={handleSave}
          onCancel={handleCancel}
        />
        
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Box sx={{ width: '60%', height: "calc(100vh - 190px)"}}>
            <LayoutComponent
              loader={false}
              svgMap={data.svgMap}
              blocks={data.blocks}
              overAllSVG={enhancedData.overAllSVG}
              svgProgressBlockWise={svgProgressBlockWise}
              activityId="All"
              list={data.list}
              selectPart={selectPart}
              setSelectPart={setSelectPart}
              selectBlock={selectBlock}
              isSelect={isSelect}
              onBlockClick={handleSvgBlockClick}
              type="ConstructionProgress"
              editMode={editMode}
            />
          </Box>
        <Box sx={{ width: '40%', p: 2, borderLeft: "2px solid #f5f5f5" , height: "calc(100vh - 190px)", overflow: "auto" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Construction Sequence</Typography>
          <TableContainer component={Paper} sx={tableContainerStyle}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  {!editMode && <TableCell>Sequence</TableCell>}
                  <TableCell>Block</TableCell>
                  <TableCell>Start Week</TableCell>
                  <TableCell>End Week</TableCell>
                  {editMode && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sequenceData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={editMode ? 4 : 5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {editMode ? "Click on blocks in the SVG to add them to the sequence" : "No sequence data available"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sequenceData.map((row, idx) => (
                    <TableRow key={row.id ? row.id : `${row.blockId}-${idx}`}>
                      {!editMode && <TableCell>{row.sequence}</TableCell>}
                      <TableCell>{row.blockName}</TableCell>
                      <TableCell>
                        {editMode && editRow === idx ? (
                          <DatePicker
                            value={timestampToDate(row.startWeek)}
                            onChange={(newValue) => handleTableChange(idx, 'startWeek', dateToYMD(newValue))}
                            shouldDisableDate={(date) => !isMonday(date)}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: { width: 120 }
                              }
                            }}
                          />
                        ) : (
                          formatDate(row.startWeek)
                        )}
                      </TableCell>
                      <TableCell>
                        {editMode && editRow === idx ? (
                          <DatePicker
                            value={timestampToDate(row.endWeek)}
                            onChange={(newValue) => handleTableChange(idx, 'endWeek', dateToYMD(newValue))}
                            shouldDisableDate={(date) => !isMonday(date)}
                            minDate={timestampToDate(row.startWeek)}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: { width: 120 }
                              }
                            }}
                          />
                        ) : (
                          formatDate(row.endWeek)
                        )}
                      </TableCell>
                      {editMode && (
                        <TableCell>
                          {editMode && editRow === idx ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                const updatedRow = sequenceData[idx];
                                handleSaveRow(updatedRow);
                              }}
                            >
                              Save
                            </Button>
                          ) : (
                            <IconButton size="small" title="Actions" onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setCurrentRowIdx(idx);
                            }}>
                              <EllipsisVertical size={18} />
                            </IconButton>
                          )}
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl) && currentRowIdx === idx}
                            onClose={() => setAnchorEl(null)}
                          >
                            <MenuItem onClick={() => {
                              handleTableEdit(idx);
                              setAnchorEl(null);
                            }}>
                              <EditIcon fontSize="small" /> Edit
                            </MenuItem>
                            <MenuItem onClick={() => {
                              handleDeleteSequence(row);
                              setAnchorEl(null);
                            }}>
                              <DeleteIcon fontSize="small" /> Delete
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Modal
          open={popupOpen}
          onClose={() => {
            setPopupOpen(false);
            setSelectedBlock(null);
            const currentHighlighted = sequenceData.map(seq => seq.blockId);
            setHighlightedBlocks(currentHighlighted);
          }}
          aria-labelledby="construction-progress-popup"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box sx={{
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: 1,
            boxShadow: 24
          }}>
            <ConstructionProgressPopup
              block={selectedBlock}
              onSubmit={handlePopupSubmit}
              onCancel={() => {
                setPopupOpen(false);
                setSelectedBlock(null);
                const currentHighlighted = sequenceData.map(seq => seq.blockId);
                setHighlightedBlocks(currentHighlighted);
              }}
            />
          </Box>
        </Modal>
      </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ConstructionProgressCard;